const PLATFORM_ALIASES = Object.freeze({
  instagram: 'reels', meta: 'reels', reels: 'reels', x: 'x', twitter: 'x', reddit: 'reddit', threads: 'threads', tiktok: 'tiktok', youtube: 'youtube'
});

const numberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const sumNullable = (left, right) => {
  const a = numberOrNull(left);
  const b = numberOrNull(right);
  if (a === null && b === null) return null;
  return (a || 0) + (b || 0);
};

export const platformDataKey = (platform) => PLATFORM_ALIASES[String(platform || '').trim().toLowerCase()] || null;

const basePlatformMetrics = (row, key) => {
  const posts = numberOrNull(row.posts);
  const views = numberOrNull(row.views);
  const reach = numberOrNull(row.reach);
  const impressions = numberOrNull(row.impressions);
  const followersGained = numberOrNull(row.followers_gained ?? row.followersGained);
  const totalFollowers = numberOrNull(row.total_followers ?? row.totalFollowers);
  const likes = numberOrNull(row.likes);
  const comments = numberOrNull(row.comments);
  const engagements = numberOrNull(row.engagements);
  const profileVisits = numberOrNull(row.profile_visits ?? row.profileVisits);
  const engagementRate = numberOrNull(row.engagement_rate ?? row.engagementRate);
  const shares = numberOrNull(row.shares);
  const saves = numberOrNull(row.saves);

  if (key === 'reels') return { posted: posts, posts, followersGained, totalFollowers, views, reach, impressions, likes, comments, shares, saves, avgViews: posts && views !== null ? Math.round(views / posts) : null };
  if (key === 'x') return { posts, impressions, reach, engagements, engagementRate, profileVisits, followersGained, totalFollowers, likes, comments, shares };
  if (key === 'reddit') return { posts, upvotes: likes, comments, impressions, reach, totalFollowers, avgUpvotes: posts && likes !== null ? Number((likes / posts).toFixed(2)) : null, avgImpressions: posts && impressions !== null ? Math.round(impressions / posts) : null };
  return { posts, impressions, reach, engagements, engagementRate, profileVisits, followersGained, totalFollowers, likes, comments, shares, saves, views };
};

const mergeSamePlatformMetrics = (current = {}, incoming = {}) => {
  const merged = { ...current };
  for (const [key, value] of Object.entries(incoming)) {
    merged[key] = key === 'engagementRate' || key.startsWith('avg') ? (value ?? merged[key] ?? null) : sumNullable(merged[key], value);
  }
  return merged;
};

export const normalizeMarketingRows = (rows = []) => {
  const weeks = new Map();
  for (const row of rows || []) {
    const dataKey = platformDataKey(row.platform);
    if (!dataKey || !row.week) continue;
    const weekKey = String(row.week);
    const existing = weeks.get(weekKey) || {
      week: weekKey, period: row.period || weekKey, year: String(row.year || weekKey.slice(0, 4)),
      source: 'live_api', quality: row.data_quality || row.quality || 'verified', note: row.notes || '',
      syncedAt: row.synced_at || row.updated_at || row.created_at || null
    };
    existing[dataKey] = mergeSamePlatformMetrics(existing[dataKey], basePlatformMetrics(row, dataKey));
    if (row.data_quality && row.data_quality !== 'verified') existing.quality = row.data_quality;
    if (row.notes) existing.note = [existing.note, row.notes].filter(Boolean).join(' | ');
    weeks.set(weekKey, existing);
  }
  return Array.from(weeks.values()).sort((a, b) => String(a.week).localeCompare(String(b.week)));
};

export const mergeMarketingRows = (historical = [], live = []) => {
  const byWeek = new Map((historical || []).map((row) => [String(row.week || row.period), { ...row }]));
  for (const incoming of live || []) {
    const key = String(incoming.week || incoming.period);
    const current = byWeek.get(key) || {};
    const merged = { ...current, ...incoming };
    for (const platformKey of ['reddit', 'x', 'reels', 'threads', 'tiktok', 'youtube']) {
      if (current[platformKey] || incoming[platformKey]) merged[platformKey] = { ...(current[platformKey] || {}), ...(incoming[platformKey] || {}) };
    }
    byWeek.set(key, merged);
  }
  return Array.from(byWeek.values()).sort((a, b) => String(a.week || a.period).localeCompare(String(b.week || b.period)));
};

